import * as React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import GlobalStyles from '@mui/joy/GlobalStyles';
import Avatar from '@mui/joy/Avatar';
import Box from '@mui/joy/Box';
import Divider from '@mui/joy/Divider';
import IconButton from '@mui/joy/IconButton';
import Input from '@mui/joy/Input';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemButton, { listItemButtonClasses } from '@mui/joy/ListItemButton';
import ListItemContent from '@mui/joy/ListItemContent';
import Typography from '@mui/joy/Typography';
import Sheet from '@mui/joy/Sheet';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import SupportRoundedIcon from '@mui/icons-material/SupportRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { VolunteerActivismRounded, SimCardDownloadRounded, ChecklistRounded, CakeRounded } from '@mui/icons-material';

import ColorSchemeToggle from './ColorSchemeToggle';
import { closeSidebar } from '../../utils';
import { useUserStore } from '../../store/useUserStore';

interface User {
  id: number;
  firebase_uid: string;
  display_name: string;
  legalName?: string;
  email: string;
  phone: string;
  photo_url?: string;
  role: string;
  created_at: string;
  updated_at: string;
}

function Toggler({
  defaultExpanded = false,
  renderToggle,
  children,
}: {
  defaultExpanded?: boolean;
  children: React.ReactNode;
  renderToggle: (params: {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  }) => React.ReactNode;
}) {
  const [open, setOpen] = React.useState(defaultExpanded);
  return (
    <React.Fragment>
      {renderToggle({ open, setOpen })}
      <Box
        sx={[
          {
            display: 'grid',
            transition: '0.2s ease',
            '& > *': {
              overflow: 'hidden',
            },
          },
          open ? { gridTemplateRows: '1fr' } : { gridTemplateRows: '0fr' },
        ]}
      >
        {children}
      </Box>
    </React.Fragment>
  );
}

export default function Sidebar() {
  let navigate = useNavigate();
  const location = useLocation();
  const { user, setUser, isAuthorized, setIsAuthorized } = useUserStore();
  const userDisplayName = user?.display_name?.trim() || user?.legalName?.trim() || 'Unknown User';

  React.useEffect(() => {
    const checkRole = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/user/role`, {
          method: "GET",
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error("Failed to check role.");
        }

        const data = await response.json();
        if (data.role !== "editor" && data.role !== "Admin") {
          navigate("/unauthorised");
        } else {
          setIsAuthorized(true);
        }
      } catch (err) {
        console.error("Role check failed:", err);
      }
    };

    checkRole();
  }, [navigate]);

  React.useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/user/me`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });

        if (!response.ok) {
          if (response.status === 401) {
            const redirectUrl = encodeURIComponent(window.location.href);
            window.location.href = `${process.env.REACT_APP_AUTH_WEB_URL}/signin?redirect=${redirectUrl}`;
            return;
          }
          throw new Error("Failed to get user.");
        }

        const data = await response.json();
        const displayName = data.display_name || data.legalName || data.name || '';
        setUser({
          id: data.id,
          firebase_uid: data.firebase_uid,
          display_name: displayName,
          legalName: data.legalName,
          email: data.email,
          phone: data.phone,
          photo_url: data.photo_url,
          role: data.role,
          created_at: data.created_at,
          updated_at: data.updated_at
        });
      } catch (err) {
        console.log("Failed to get user.");
      } finally {

      }
    }

    fetchUsers();
  }, []);

  return (
    <Sheet
      className="Sidebar"
      sx={{
        position: { xs: 'fixed', md: 'sticky' },
        transform: {
          xs: 'translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1)))',
          md: 'none',
        },
        transition: 'transform 0.4s, width 0.4s',
        zIndex: 10000,
        height: '100dvh',
        width: 'var(--Sidebar-width)',
        top: 0,
        p: 2,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        borderRight: '1px solid',
        borderColor: 'divider',
      }}
    >
      <GlobalStyles
        styles={(theme) => ({
          ':root': {
            '--Sidebar-width': '220px',
            [theme.breakpoints.up('lg')]: {
              '--Sidebar-width': '240px',
            },
          },
        })}
      />
      <Box
        className="Sidebar-overlay"
        sx={{
          position: 'fixed',
          zIndex: 9998,
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          opacity: 'var(--SideNavigation-slideIn)',
          backgroundColor: 'var(--joy-palette-background-backdrop)',
          transition: 'opacity 0.4s',
          transform: {
            xs: 'translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1) + var(--SideNavigation-slideIn, 0) * var(--Sidebar-width, 0px)))',
            lg: 'translateX(-100%)',
          },
        }}
        onClick={() => closeSidebar()}
      />
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <img alt="ISKCON Kanpur logo" src="https://cdn.iskconkanpur.org/static/brand/isk-logo-circle.png" style={{ width: 50, height: 50 }} />
        <Typography level="title-lg">ISKCON Kanpur</Typography>
        <ColorSchemeToggle sx={{ ml: 'auto' }} />
      </Box>
      <Input size="sm" startDecorator={<SearchRoundedIcon />} placeholder="Search" />
      <Box
        sx={{
          minHeight: 0,
          overflow: 'hidden auto',
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          [`& .${listItemButtonClasses.root}`]: {
            gap: 1.5,
          },
        }}
      >
        <List
          size="sm"
          sx={{
            gap: 1,
            '--List-nestedInsetStart': '30px',
            '--ListItem-radius': (theme) => theme.vars.radius.sm,
          }}
        >

          <ListItem>
            <ListItemButton selected={location.pathname === "/"} onClick={() => {
              navigate("/")
            }} >
              <DashboardRoundedIcon />
              <ListItemContent>
                <Typography level="title-sm">Dashboard</Typography>
              </ListItemContent>
            </ListItemButton>
          </ListItem>

          <ListItem nested>
            <Toggler
              defaultExpanded={location.pathname.startsWith("/donation")}
              renderToggle={({ open, setOpen }) => (
                <ListItemButton onClick={() => setOpen(!open)}>
                  <VolunteerActivismRounded />
                  <ListItemContent>
                    <Typography level="title-sm">Donation</Typography>
                  </ListItemContent>
                  <KeyboardArrowDownIcon
                    sx={[
                      open
                        ? {
                          transform: 'rotate(180deg)',
                        }
                        : {
                          transform: 'none',
                        },
                    ]}
                  />
                </ListItemButton>
              )}
            >
              <List sx={{ gap: 0.5 }}>
                <ListItem sx={{ mt: 0.5 }}>
                  <ListItemButton selected={location.pathname === "/donation"} onClick={() => {
                    navigate("/donation")
                  }} >Records</ListItemButton>
                </ListItem>
                <ListItem>
                  <ListItemButton selected={location.pathname === "/donation/add"} onClick={() => {
                    navigate("/donation/add")
                  }} >Add</ListItemButton>
                </ListItem>
                <ListItem>
                  <ListItemButton selected={location.pathname === "/donation/donor"} onClick={() => {
                    navigate("/donation/donor")
                  }} >Donor</ListItemButton>
                </ListItem>
                <ListItem>
                  <ListItemButton selected={location.pathname === "/donation/donor/add"} onClick={() => {
                    navigate("/donation/donor/add")
                  }} >Add Donor</ListItemButton>
                </ListItem>
                <ListItem>
                  <ListItemButton selected={location.pathname === "/donation/options"} onClick={() => {
                    navigate("/donation/options")
                  }} >Options</ListItemButton>
                </ListItem>
              </List>
            </Toggler>
          </ListItem>

          <ListItem>
            <ListItemButton selected={location.pathname === "/report"} onClick={() => {
              navigate("/report")
            }} >
              <SimCardDownloadRounded />
              <ListItemContent>
                <Typography level="title-sm">Report</Typography>
              </ListItemContent>
            </ListItemButton>
          </ListItem>

          <ListItem>
            <ListItemButton selected={location.pathname === "/nitya-seva"} onClick={() => {
              navigate("/nitya-seva")
            }} >
              <FavoriteRoundedIcon />
              <ListItemContent>
                <Typography level="title-sm">Nitya Seva</Typography>
              </ListItemContent>
            </ListItemButton>
          </ListItem>

          <ListItem>
            <ListItemButton selected={location.pathname === "/celebrations"} onClick={() => {
              navigate("/celebrations")
            }} >
              <CakeRounded />
              <ListItemContent>
                <Typography level="title-sm">Celebrations</Typography>
              </ListItemContent>
            </ListItemButton>
          </ListItem>

          <ListItem>
            <ListItemButton selected={location.pathname === "/audit-logs"} onClick={() => {
              navigate("/audit-logs")
            }} >
              <ChecklistRounded />
              <ListItemContent>
                <Typography level="title-sm">Audit Logs</Typography>
              </ListItemContent>
            </ListItemButton>
          </ListItem>
          {/* <ListItem nested>
            <Toggler
              defaultExpanded={location.pathname.startsWith("/expense")}
              renderToggle={({ open, setOpen }) => (
                <ListItemButton onClick={() => setOpen(!open)}>
                  <PaymentsRounded />
                  <ListItemContent>
                    <Typography level="title-sm">Expense</Typography>
                  </ListItemContent>
                  <KeyboardArrowDownIcon
                    sx={[
                      open
                        ? {
                            transform: 'rotate(180deg)',
                          }
                        : {
                            transform: 'none',
                          },
                    ]}
                  />
                </ListItemButton>
              )}
            >
              <List sx={{ gap: 0.5 }}>
                <ListItem sx={{ mt: 0.5 }}>
                  <ListItemButton
                    role="menuitem"
                    component="a"
                    href="/joy-ui/getting-started/templates/profile-dashboard/"
                  >
                    My profile
                  </ListItemButton>
                </ListItem>
                <ListItem>
                  <ListItemButton>Create a new user</ListItemButton>
                </ListItem>
                <ListItem>
                  <ListItemButton>Roles & permission</ListItemButton>
                </ListItem>
              </List>
            </Toggler>
          </ListItem> */}
        </List>
        <List
          size="sm"
          sx={{
            mt: 'auto',
            flexGrow: 0,
            '--ListItem-radius': (theme) => theme.vars.radius.sm,
            '--List-gap': '8px',
            mb: 2,
          }}
        >
          <ListItem>
            <ListItemButton>
              <SupportRoundedIcon />
              Support
            </ListItemButton>
          </ListItem>
          <ListItem>
            <ListItemButton>
              <SettingsRoundedIcon />
              Settings
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
      <Divider />
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Avatar
          variant="outlined"
          size="sm"
          src={user?.photo_url || ""}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography level="title-sm">{userDisplayName}</Typography>
          <Typography level="body-xs">{user?.email || "No email"}</Typography>
        </Box>
        <IconButton
          size="sm"
          variant="plain"
          color="neutral"
          onClick={() => {
            const redirectUrl = encodeURIComponent(window.location.href);
            window.location.href = `${process.env.REACT_APP_AUTH_WEB_URL}/signout?redirect=${redirectUrl}`;
          }}
        >
          <LogoutRoundedIcon />
        </IconButton>
      </Box>
    </Sheet>
  );
}
